import React, { useState, useEffect, useRef } from 'react';
import { getConversations, updateConversation, apiFetch } from '../services/api';
import './CustomerService.css';

function CustomerService() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [filter, setFilter] = useState('aberto');
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // Carregar conversas
  useEffect(() => {
    loadConversations();
    setupWebSocket();
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [filter]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data = await getConversations({ status: filter }, '-last_message_time', 100);
      setConversations(data);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupWebSocket = () => {
    const ws = new WebSocket('');
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'NEW_MESSAGE') {
        // Atualizar conversa em tempo real
        updateConversationInList(data.conversationId, data.message);
      }
    };
    wsRef.current = ws;
  };

  const updateConversationInList = (convId, newMessage) => {
    setConversations(prev => prev.map(conv => {
      if (conv.id === convId) {
        return {
          ...conv,
          messages: [...(conv.messages || []), newMessage],
          last_message: newMessage.content,
          last_message_time: newMessage.timestamp,
        };
      }
      return conv;
    }));
    
    if (selectedConversation?.id === convId) {
      setSelectedConversation(prev => ({
        ...prev,
        messages: [...(prev.messages || []), newMessage],
        last_message: newMessage.content,
        last_message_time: newMessage.timestamp,
      }));
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation || sending) return;
    
    setSending(true);
    const tempMessage = {
      id: Date.now(),
      sender: 'agent',
      content: messageText,
      timestamp: new Date().toISOString(),
      type: 'text',
      temporary: true,
    };
    
    // Adicionar mensagem temporária
    setSelectedConversation(prev => ({
      ...prev,
      messages: [...(prev.messages || []), tempMessage],
      last_message: messageText,
      last_message_time: new Date().toISOString(),
    }));
    
    setMessageText('');
    
    try {
      await apiFetch(`/api/conversations/${selectedConversation.id}/send`, {
        method: 'POST',
        body: JSON.stringify({ message: messageText }),
      });
      
      // Remover flag temporária
      setSelectedConversation(prev => ({
        ...prev,
        messages: prev.messages.map(m => 
          m.id === tempMessage.id ? { ...m, temporary: false } : m
        ),
      }));
      
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem: ' + error.message);
      // Remover mensagem temporária
      setSelectedConversation(prev => ({
        ...prev,
        messages: prev.messages.filter(m => m.id !== tempMessage.id),
      }));
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const config = {
      aberto: { label: 'Aberto', class: 'status-open' },
      pendente: { label: 'Pendente', class: 'status-pending' },
      finalizado: { label: 'Finalizado', class: 'status-closed' },
    };
    const c = config[status] || config.aberto;
    return <span className={`conv-status ${c.class}`}>{c.label}</span>;
  };

  return (
    <div className="customer-service-page">
      <div className="cs-header">
        <h1>Atendimento WhatsApp</h1>
        <div className="cs-filters">
          <button 
            className={`filter-btn ${filter === 'aberto' ? 'active' : ''}`}
            onClick={() => setFilter('aberto')}
          >
            Em Aberto
          </button>
          <button 
            className={`filter-btn ${filter === 'pendente' ? 'active' : ''}`}
            onClick={() => setFilter('pendente')}
          >
            Pendentes
          </button>
          <button 
            className={`filter-btn ${filter === 'finalizado' ? 'active' : ''}`}
            onClick={() => setFilter('finalizado')}
          >
            Finalizados
          </button>
        </div>
      </div>

      <div className="cs-container">
        {/* Lista de conversas */}
        <div className="conversations-list">
          {loading ? (
            <div className="loading-state">Carregando conversas...</div>
          ) : conversations.length === 0 ? (
            <div className="empty-state">Nenhuma conversa encontrada</div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.id}
                className={`conversation-item ${selectedConversation?.id === conv.id ? 'active' : ''}`}
                onClick={() => setSelectedConversation(conv)}
              >
                <div className="conv-avatar">
                  {conv.client_name?.charAt(0) || '?'}
                </div>
                <div className="conv-info">
                  <div className="conv-name">{conv.client_name || 'Cliente'}</div>
                  <div className="conv-last-msg">{conv.last_message?.substring(0, 40) || '...'}</div>
                </div>
                <div className="conv-meta">
                  <div className="conv-time">{formatTime(conv.last_message_time)}</div>
                  {getStatusBadge(conv.status)}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Área de chat */}
        <div className="chat-area">
          {selectedConversation ? (
            <>
              <div className="chat-header">
                <div className="chat-client-info">
                  <div className="chat-avatar">
                    {selectedConversation.client_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className="chat-client-name">
                      {selectedConversation.client_name || 'Cliente'}
                    </div>
                    <div className="chat-client-phone">
                      {selectedConversation.client_phone}
                    </div>
                  </div>
                </div>
                {getStatusBadge(selectedConversation.status)}
              </div>

              <div className="messages-container">
                {(selectedConversation.messages || []).map((msg, idx) => (
                  <div
                    key={msg.id || idx}
                    className={`message ${msg.sender === 'agent' ? 'message-agent' : 'message-client'}`}
                  >
                    <div className="message-bubble">
                      <div className="message-text">{msg.content}</div>
                      <div className="message-time">{formatTime(msg.timestamp)}</div>
                    </div>
                    {msg.temporary && <span className="message-sending">⏳</span>}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="chat-input-area">
                <textarea
                  className="chat-input"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Digite sua mensagem..."
                  rows={3}
                  disabled={sending}
                />
                <button 
                  className="send-btn" 
                  onClick={handleSendMessage}
                  disabled={!messageText.trim() || sending}
                >
                  {sending ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </>
          ) : (
            <div className="no-conversation-selected">
              <span>💬</span>
              <p>Selecione uma conversa para começar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CustomerService;